import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { Crown, Flame, Check, Lock, Bell, CreditCard, HelpCircle, FileText, LogOut, Trash2, User as UserIcon, Mail, Phone, ChevronRight, Save, Sparkles } from "lucide-react";
import { Input } from "../components/ui/input";
import { useAuth } from "../lib/auth-context";
import { api } from "../lib/api";
import { toast } from "sonner";

const Section = ({ title, children, testid }) => (
  <div className="mt-6" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38] mb-3">{title}</p>
    <div className="rounded-3xl bg-white border border-[#E2EBE6] divide-y divide-[#E2EBE6]">{children}</div>
  </div>
);

const Row = ({ icon: Icon, label, value, onClick, testid }) => (
  <button onClick={onClick} disabled={!onClick} data-testid={testid}
    className="w-full flex items-center gap-3 px-5 py-4 text-left disabled:cursor-default">
    {Icon && <Icon className="w-4 h-4 text-[#234B38] shrink-0" strokeWidth={1.5} />}
    <span className="text-sm text-[#1A2E24] flex-1">{label}</span>
    {value && <span className="text-xs text-[#5C7568]">{value}</span>}
    {onClick && <ChevronRight className="w-4 h-4 text-[#5C7568]" />}
  </button>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user, isPro, trialActive, trialDaysRemaining, logout, refresh, updateProfile, deleteAccount } = useAuth();
  const [streak, setStreak] = useState(0);
  const [days, setDays] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", current_password: "" });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [notif, setNotif] = useState({
    daily_reminder: true,
    morning_enabled: true,
    afternoon_enabled: true,
    evening_enabled: true,
    morning_time: "08:00",
    afternoon_time: "14:00",
    evening_time: "20:00",
    trial_ending_enabled: true,
    renewal_reminder_enabled: true,
    streak_reminder_enabled: true,
  });
  const [showDelete, setShowDelete] = useState(false);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    setForm({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "", current_password: "" });
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const [a, n, s, inv] = await Promise.all([
          api.myAnalytics(),
          api.getNotifications(),
          api.subscriptionStatus(),
          api.listInvoices(),
        ]);
        setStreak(a.data.current_streak || 0);
        setDays(a.data.unique_active_days || 0);
        setNotif({
          daily_reminder: n.data.daily_reminder ?? true,
          morning_enabled: n.data.morning_enabled ?? true,
          afternoon_enabled: n.data.afternoon_enabled ?? true,
          evening_enabled: n.data.evening_enabled ?? true,
          morning_time: n.data.morning_time || "08:00",
          afternoon_time: n.data.afternoon_time || "14:00",
          evening_time: n.data.evening_time || "20:00",
          trial_ending_enabled: n.data.trial_ending_enabled ?? true,
          renewal_reminder_enabled: n.data.renewal_reminder_enabled ?? true,
          streak_reminder_enabled: n.data.streak_reminder_enabled ?? true,
        });
        setSub(s.data);
        setInvoices(inv.data.items || []);
      } catch { /* silent */ }
    })();
  }, []);

  const saveAccount = async () => {
    const emailChanged = form.email && form.email.toLowerCase() !== (user?.email || "").toLowerCase();
    if (emailChanged && user?.auth_provider === "password" && !form.current_password) {
      toast.error("Enter your current password to change email");
      return;
    }
    try {
      const payload = { name: form.name, phone: form.phone };
      if (emailChanged) {
        payload.email = form.email;
        if (form.current_password) payload.current_password = form.current_password;
      }
      await updateProfile(payload);
      setForm({ ...form, current_password: "" });
      setEditMode(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const changePassword = async () => {
    try {
      await api.changePassword(pwd);
      setPwd({ current_password: "", new_password: "" });
      setShowPwd(false);
      toast.success("Password updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveNotif = async () => {
    try {
      await api.updateNotifications(notif);
      toast.success("Notification settings saved");
    } catch { toast.error("Save failed"); }
  };

  const handleDelete = async () => {
    try {
      await deleteAccount();
      toast("Account deleted");
      navigate("/login", { replace: true });
    } catch { toast.error("Delete failed"); }
  };

  if (!user) return null;

  return (
    <div className="app-shell" data-testid="profile-page">
      <Header />
      <section className="px-6 pt-2 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#234B38]">Your Practice</p>
        <h2 className="text-3xl font-medium text-[#1A2E24] mt-1 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
          Steady, day by day
        </h2>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5" data-testid="stat-streak">
            <Flame className="w-5 h-5 text-[#234B38]" strokeWidth={1.5} />
            <p className="text-3xl font-medium text-[#1A2E24] mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>{streak}</p>
            <p className="text-xs text-[#5C7568] uppercase tracking-wider mt-1">Streak</p>
          </div>
          <div className="rounded-3xl bg-white border border-[#E2EBE6] p-5" data-testid="stat-days">
            <Check className="w-5 h-5 text-[#234B38]" strokeWidth={1.5} />
            <p className="text-3xl font-medium text-[#1A2E24] mt-3" style={{ fontFamily: "Outfit, sans-serif" }}>{days}</p>
            <p className="text-xs text-[#5C7568] uppercase tracking-wider mt-1">Active days</p>
          </div>
        </div>

        {/* Account info */}
        <Section title="Account Information" testid="account-section">
          <div className="px-5 py-4 space-y-3">
            {editMode ? (
              <>
                <div className="rounded-2xl bg-[#FDFBF7] border border-[#E2EBE6] flex items-center px-3 gap-2">
                  <UserIcon className="w-4 h-4 text-[#5C7568]" />
                  <Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                </div>
                <div className="rounded-2xl bg-[#FDFBF7] border border-[#E2EBE6] flex items-center px-3 gap-2">
                  <Mail className="w-4 h-4 text-[#5C7568]" />
                  <Input data-testid="account-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                </div>
                {form.email && form.email.toLowerCase() !== (user?.email || "").toLowerCase() && user?.auth_provider === "password" && (
                  <div className="rounded-2xl bg-[#F5D0D6]/30 border border-[#F5D0D6] flex items-center px-3 gap-2" data-testid="email-confirm-wrap">
                    <Lock className="w-4 h-4 text-[#D6336C]" />
                    <Input data-testid="account-email-password-input" type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Confirm current password to change email" className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                  </div>
                )}
                <div className="rounded-2xl bg-[#FDFBF7] border border-[#E2EBE6] flex items-center px-3 gap-2">
                  <Phone className="w-4 h-4 text-[#5C7568]" />
                  <Input data-testid="account-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="border-0 bg-transparent focus-visible:ring-0 px-0" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveAccount} data-testid="account-save-btn" className="flex-1 rounded-full bg-[#234B38] text-white py-2.5 text-sm font-medium flex items-center justify-center gap-1.5">
                    <Save className="w-3.5 h-3.5" /> Save
                  </button>
                  <button onClick={() => { setEditMode(false); setForm({ ...form, current_password: "", email: user?.email || "" }); }} className="flex-1 rounded-full bg-white border border-[#E2EBE6] text-[#5C7568] py-2.5 text-sm font-medium">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <UserIcon className="w-4 h-4 text-[#234B38]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Name</p>
                    <p className="text-sm text-[#1A2E24]" data-testid="account-name">{user.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#234B38]" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Email</p>
                    <p className="text-sm text-[#1A2E24] truncate" data-testid="account-email">{user.email}</p>
                  </div>
                </div>
                {user.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#234B38]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Phone</p>
                      <p className="text-sm text-[#1A2E24]">{user.phone}</p>
                    </div>
                  </div>
                )}
                <button onClick={() => setEditMode(true)} data-testid="account-edit-btn" className="text-xs font-semibold uppercase tracking-wider text-[#234B38]">
                  Edit
                </button>
              </>
            )}
          </div>
          {user.auth_provider === "password" && (
            <div className="px-5 py-4">
              {showPwd ? (
                <div className="space-y-2">
                  <Input data-testid="current-password" type="password" placeholder="Current password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="rounded-2xl border-[#E2EBE6] bg-[#FDFBF7]" />
                  <Input data-testid="new-password" type="password" placeholder="New password (min 6)" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="rounded-2xl border-[#E2EBE6] bg-[#FDFBF7]" />
                  <div className="flex gap-2">
                    <button onClick={changePassword} data-testid="change-password-btn" className="flex-1 rounded-full bg-[#234B38] text-white py-2.5 text-sm font-medium">Update password</button>
                    <button onClick={() => setShowPwd(false)} className="flex-1 rounded-full bg-white border border-[#E2EBE6] text-[#5C7568] py-2.5 text-sm font-medium">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowPwd(true)} data-testid="show-change-password-btn" className="text-xs font-semibold uppercase tracking-wider text-[#234B38]">
                  Change password
                </button>
              )}
            </div>
          )}
        </Section>

        {/* Subscription */}
        <Section title="Subscription" testid="subscription-section">
          <div className="px-5 py-4">
            <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Current plan</p>
            <p className="text-base font-medium text-[#1A2E24] mt-1 flex items-center gap-2" data-testid="current-plan">
              {sub?.subscription_status === "TRIAL" ? (<><Sparkles className="w-4 h-4 text-[#D6336C]" /> 7-day Pro Trial</>)
                : sub?.subscription_status === "PRO_ACTIVE" ? (<><Crown className="w-4 h-4 text-[#234B38]" /> Pro {sub?.plan_type === "yearly" ? "Yearly" : sub?.plan_type === "monthly" ? "Monthly" : ""}</>)
                : sub?.subscription_status === "PRO_CANCELLED" ? (<><Crown className="w-4 h-4 text-[#234B38]" /> Pro · Cancelling</>)
                : sub?.subscription_status === "PAYMENT_FAILED" ? (<><Lock className="w-4 h-4 text-[#D6336C]" /> Payment Failed</>)
                : (<><Lock className="w-4 h-4 text-[#5C7568]" /> Free</>)}
            </p>
            {sub?.amount_display && (
              <p className="text-xs text-[#5C7568] mt-0.5" data-testid="current-plan-amount">{sub.amount_display}</p>
            )}
            {trialActive && (
              <div className="mt-3 rounded-2xl bg-[#F5D0D6]/30 px-3 py-2 text-xs text-[#1A2E24]" data-testid="trial-status">
                <span className="font-semibold">{trialDaysRemaining} day{trialDaysRemaining === 1 ? "" : "s"} left.</span>{" "}
                Trial ends on {sub?.trial_end?.slice(0,10)}
              </div>
            )}
            <p className="text-[10px] text-[#5C7568] uppercase tracking-wider mt-4">
              {sub?.subscription_status === "PRO_CANCELLED" ? "Access ends" : "Next billing date"}
            </p>
            <p className="text-sm text-[#1A2E24] mt-1" data-testid="next-billing">{sub?.next_billing_date ? sub.next_billing_date.slice(0,10) : "—"}</p>
            <button
              onClick={() => navigate("/subscription")}
              data-testid="manage-subscription-btn"
              className="mt-4 w-full rounded-full bg-[#234B38] hover:bg-[#1A382A] text-white py-2.5 text-sm font-medium"
            >
              {sub?.is_pro ? "Manage subscription" : "Upgrade to Pro"}
            </button>
          </div>
        </Section>

        {/* Notifications */}
        <Section title="Notifications" testid="notifications-section">
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#234B38]" />
                <p className="text-sm text-[#1A2E24]">Daily reminder</p>
              </div>
              <button
                data-testid="notif-toggle-btn"
                onClick={() => setNotif({ ...notif, daily_reminder: !notif.daily_reminder })}
                className={`w-11 h-6 rounded-full transition-colors relative ${notif.daily_reminder ? "bg-[#234B38]" : "bg-[#E2EBE6]"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${notif.daily_reminder ? "translate-x-5" : ""}`} />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "morning", label: "Morning" },
                { key: "afternoon", label: "Afternoon" },
                { key: "evening", label: "Evening" },
              ].map((t) => {
                const enabledKey = `${t.key}_enabled`;
                const timeKey = `${t.key}_time`;
                const slotOn = notif.daily_reminder && notif[enabledKey];
                return (
                  <div key={t.key} className="rounded-2xl border border-[#E2EBE6] bg-[#FDFBF7] p-2.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] text-[#5C7568] uppercase tracking-wider">{t.label}</label>
                      <button
                        type="button"
                        data-testid={`notif-${t.key}-toggle`}
                        disabled={!notif.daily_reminder}
                        onClick={() => setNotif({ ...notif, [enabledKey]: !notif[enabledKey] })}
                        className={`w-8 h-4 rounded-full transition-colors relative disabled:opacity-40 ${
                          slotOn ? "bg-[#234B38]" : "bg-[#E2EBE6]"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform ${slotOn ? "translate-x-4" : ""}`} />
                      </button>
                    </div>
                    <Input
                      data-testid={`notif-${timeKey}`}
                      type="time"
                      value={notif[timeKey]}
                      onChange={(e) => setNotif({ ...notif, [timeKey]: e.target.value })}
                      disabled={!slotOn}
                      className="rounded-xl border-[#E2EBE6] bg-white mt-2 disabled:opacity-50 h-8 text-xs"
                    />
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#5C7568] italic">
              Master OFF disables all reminders. Each slot can be toggled independently when master is ON.
            </p>

            {/* Lifecycle reminders (Android / iOS / PWA push) */}
            <div className="mt-4 pt-3 border-t border-[#E2EBE6]" data-testid="lifecycle-reminders">
              <p className="text-[10px] text-[#5C7568] uppercase tracking-[0.2em] font-semibold mb-2">Lifecycle reminders</p>
              {[
                { key: "trial_ending_enabled", label: "Trial ending", hint: "Heads-up 2 days before your free trial ends." },
                { key: "renewal_reminder_enabled", label: "Subscription renewal", hint: "Reminder 2 days before your plan renews." },
                { key: "streak_reminder_enabled", label: "Streak reminder", hint: "Gentle nudge if you skip a reset day." },
              ].map((r) => (
                <div key={r.key} className="flex items-start justify-between gap-3 py-2" data-testid={`lifecycle-row-${r.key}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-[#1A2E24]">{r.label}</p>
                    <p className="text-[10px] text-[#5C7568] mt-0.5 leading-snug">{r.hint}</p>
                  </div>
                  <button
                    type="button"
                    data-testid={`lifecycle-toggle-${r.key}`}
                    onClick={() => setNotif({ ...notif, [r.key]: !notif[r.key] })}
                    className={`w-11 h-6 rounded-full transition-colors relative shrink-0 ${notif[r.key] ? "bg-[#234B38]" : "bg-[#E2EBE6]"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${notif[r.key] ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={saveNotif} data-testid="notif-save-btn" className="rounded-full bg-[#234B38] text-white px-4 py-2 text-xs font-semibold mt-2">Save</button>
          </div>
        </Section>

        {/* About Daily Reset — re-show welcome / app info */}
        <Section title="About Daily Reset" testid="about-section">
          <button
            onClick={() => { try { localStorage.removeItem("maaya_welcome_seen_v1"); } catch { /* localStorage blocked */ } window.location.href = "/login"; }}
            data-testid="rewatch-welcome-btn"
            className="w-full text-left px-5 py-4 hover:bg-[#FDFBF7] transition-colors"
          >
            <p className="text-sm text-[#1A2E24]">Re-watch welcome tour</p>
            <p className="text-[10px] text-[#5C7568] mt-0.5">See the intro screens again.</p>
          </button>
        </Section>

        {/* Billing & Payments */}
        <Section title="Billing & Payments" testid="billing-section">
          <div className="px-5 py-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-4 h-4 text-[#234B38]" />
              <div className="flex-1">
                <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Payment method</p>
                <p className="text-sm text-[#1A2E24] mt-0.5 capitalize" data-testid="billing-payment-method">
                  {sub?.payment_method || "—"}
                </p>
              </div>
            </div>
          </div>
          <div className="px-5 py-4">
            <p className="text-[10px] text-[#5C7568] uppercase tracking-wider mb-2">Invoices & Receipts</p>
            {invoices.length === 0 ? (
              <p className="text-sm text-[#5C7568]" data-testid="invoices-empty">No invoices yet.</p>
            ) : (
              <div className="space-y-2" data-testid="invoices-list">
                {invoices.map((inv) => {
                  const isEvent = inv.type === "cancellation" || inv.type === "plan_change";
                  return (
                    <div key={inv.id} className="rounded-2xl border border-[#E2EBE6] px-3 py-2.5 flex items-center justify-between gap-2" data-testid={`invoice-${inv.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-[#1A2E24] truncate">{inv.invoice_number}</p>
                        <p className="text-[10px] text-[#5C7568] uppercase tracking-wider mt-0.5">
                          {inv.plan_type === "yearly" ? "Yearly" : inv.plan_type === "monthly" ? "Monthly" : "—"} · {(inv.payment_date || "").slice(0,10)}
                        </p>
                        {inv.message ? (
                          <p className="text-[10px] text-[#5C7568] mt-0.5 truncate">{inv.message}</p>
                        ) : (
                          <p className="text-[10px] text-[#5C7568] truncate">Txn: {inv.transaction_id}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-[#234B38]">{inv.amount_display}</p>
                        <p className={`text-[9px] uppercase tracking-wider font-semibold ${
                          inv.status === "paid" ? "text-[#234B38]"
                          : inv.status === "cancelled" ? "text-[#D6336C]"
                          : "text-[#5C7568]"
                        }`}>{inv.status}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="px-5 py-3 text-[10px] text-[#5C7568] italic">
            Payments processed securely by Razorpay · UPI, Cards, Net Banking, Wallets. Receipts available for future email delivery.
          </div>
          <a href="mailto:dailyreset@maayaveda.life" className="block px-5 py-3 text-[11px] text-[#234B38] font-medium border-t border-[#E2EBE6]">
            Billing questions → dailyreset@maayaveda.life
          </a>
        </Section>

        {/* Help & Support */}
        <Section title="Help & Support" testid="help-section">
          <a href="mailto:dailyreset@maayaveda.life" className="block px-5 py-4" data-testid="support-email-row">
            <p className="text-[10px] text-[#5C7568] uppercase tracking-wider">Support email</p>
            <p className="text-sm text-[#234B38] font-medium mt-1">dailyreset@maayaveda.life</p>
            <p className="text-[10px] text-[#5C7568] mt-1">Email-only support · we reply within 48 hours.</p>
          </a>
        </Section>

        {/* Legal */}
        <Section title="Legal" testid="legal-section">
          <Link to="/terms" data-testid="link-terms"><Row label="Terms & Conditions" onClick={() => {}} /></Link>
          <Link to="/privacy" data-testid="link-privacy"><Row label="Privacy Policy" onClick={() => {}} /></Link>
          <Link to="/refund-policy" data-testid="link-refund"><Row label="Refund Policy" onClick={() => {}} /></Link>
          <Link to="/medical-disclaimer" data-testid="link-medical"><Row label="Medical Disclaimer" onClick={() => {}} /></Link>
        </Section>

        {/* Logout + Delete */}
        <div className="mt-6 space-y-3">
          <button onClick={logout} data-testid="logout-btn" className="w-full rounded-full bg-white border border-[#E2EBE6] text-[#1A2E24] py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-[#E2EBE6]/40 transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
          {showDelete ? (
            <div className="rounded-3xl bg-[#F5D0D6]/30 border border-[#F5D0D6] p-5" data-testid="delete-confirm">
              <p className="text-sm text-[#1A2E24]">Delete your account and all data? This cannot be undone.</p>
              <div className="flex gap-2 mt-3">
                <button onClick={handleDelete} data-testid="delete-confirm-btn" className="flex-1 rounded-full bg-[#D6336C] text-white py-2.5 text-sm font-medium">Yes, delete</button>
                <button onClick={() => setShowDelete(false)} className="flex-1 rounded-full bg-white border border-[#E2EBE6] text-[#5C7568] py-2.5 text-sm font-medium">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowDelete(true)} data-testid="delete-account-btn" className="w-full rounded-full bg-white border border-[#F5D0D6] text-[#D6336C] py-3.5 font-medium flex items-center justify-center gap-2 hover:bg-[#F5D0D6]/30 transition-colors">
              <Trash2 className="w-4 h-4" /> Delete account
            </button>
          )}
        </div>

        <p className="text-xs text-[#5C7568] mt-6 text-center leading-relaxed">
          Maayaveda · A Maayaverse practice
        </p>
      </section>
    </div>
  );
};

export default Profile;
